import { json } from "@remix-run/node";
import { useLoaderData, useSearchParams } from "@remix-run/react";
import {
  Box,
  Code,
  Heading,
  Text,
  Link,
  SimpleGrid,
  VStack,
  UnorderedList,
  ListItem as UnorderedListItem,
} from "@chakra-ui/react";

import {
  List,
  ListItem,
  ListItemHeader,
  ListItemContent,
} from "#app/components/layouts/FormList.jsx";
import { Layout, Header, Content } from "#app/components/layouts/AppPage.jsx";

import { FacilitySearchCombobox } from "#app/components/index.jsx";
import { useHelpTextComponent } from "#app/components/RequestForm/HelpText.jsx";
import {
  COUNTRIES,
  COUNTRY_CODES,
  RESERVATION_SYSTEM_COUNT,
  sortReservationSystems,
} from "#app/config/countries.js";
import { db } from "#app/utils/db.server.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";

// The reservation-system directory is sourced from countries.js and grouped
// into two desktop columns so related regions sit together:
// Canada + United States on the left, Australia + New Zealand on the right.
// Each country lists national systems first, then state/provincial,
// municipal, and private, alphabetical within each tier.
const DIRECTORY_COLUMNS = [
  ["ca", "us"],
  ["au", "nz"],
].map((codes) =>
  codes.map((code) => ({
    country: COUNTRIES[code].label,
    parks: sortReservationSystems(COUNTRIES[code].reservationSystems),
  })),
);

const SYSTEMS_BY_SCANNER = Object.fromEntries(
  COUNTRY_CODES.flatMap((code) =>
    COUNTRIES[code].reservationSystems.flatMap((system) =>
      system.scanners.map((scanner) => [scanner, system]),
    ),
  ),
);

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  const headlineSystems = [
    "ParksCanada",
    "RecDotGov",
    "ReserveCalifornia",
    "NSWParks",
    "NZDOC",
  ].map((scanner) => SYSTEMS_BY_SCANNER[scanner].name);

  return getSeoMeta({
    title: `Create a Request — watch campgrounds across ${RESERVATION_SYSTEM_COUNT} booking systems in Canada, the US, Australia & NZ`,
    description: `Create campground alerts for ${headlineSystems.join(", ")}, and more across four countries.`,
    url: `${baseUrl}/new`,
    image: getDefaultOgImage(baseUrl),
  });
}

export async function loader() {
  const scopedSystems = COUNTRY_CODES.flatMap((code) =>
    COUNTRIES[code].reservationSystems.filter(
      (system) => system.facilitySubName,
    ),
  );
  const [agencies, scopedCounts] = await Promise.all([
    db.agency.findMany({
      select: {
        scanner: true,
        _count: { select: { facilitys: true } },
      },
    }),
    Promise.all(
      scopedSystems.map(async (system) => [
        system.facilitySubName,
        await db.facility.count({
          where: {
            subName: system.facilitySubName,
            agency: { scanner: { in: system.scanners } },
          },
        }),
      ]),
    ),
  ]);

  return json({
    facilityCountsByScanner: Object.fromEntries(
      agencies.map((agency) => [agency.scanner, agency._count.facilitys]),
    ),
    scopedFacilityCounts: Object.fromEntries(scopedCounts),
  });
}

export default function New() {
  const { facilityCountsByScanner, scopedFacilityCounts } = useLoaderData();
  const HelpTextComponent = useHelpTextComponent();
  const [searchParams] = useSearchParams();
  const countryParam = searchParams.get("country");
  const initialCountry = COUNTRY_CODES.includes(countryParam)
    ? countryParam
    : undefined;

  return (
    <Layout>
      <Header title="Create a request" />
      <Content>
        <List>
          <ListItem>
            <ListItemHeader
              title={HelpTextComponent}
              titleProps={{
                fontSize: "md",
                fontWeight: "regular",
                w: "100%",
                as: Box,
              }}
            />
          </ListItem>
          <ListItem>
            <ListItemContent>
              <FacilitySearchCombobox
                showCountryPills
                country={initialCountry}
                pillsPlacement="bottom"
                showHelper={false}
                labelFontWeight="normal"
              />
            </ListItemContent>
          </ListItem>
          <ListItem direction="column" mt={{ base: 4, md: 8 }}>
            <ListItemHeader
              title="Campground coverage by booking system"
              titleProps={{ as: "h2", w: "100%" }}
              subtitle={`Browse one of the ${RESERVATION_SYSTEM_COUNT} booking systems Schnerp watches, then search for your campground above.`}
              subtitleProps={{ w: "100%" }}
            />
            <ListItemContent Component={Box} p={0}>
              {/* Two fixed columns on desktop: Canada + US, then Australia + NZ. */}
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacingX={10}
                spacingY={6}
              >
                {DIRECTORY_COLUMNS.map((column) => (
                  <VStack key={column[0].country} align="stretch" spacing={6}>
                    {column.map(({ country, parks }) => (
                      <Box key={country}>
                        <Heading as="h3" size="sm" mb={2}>
                          {country}
                        </Heading>
                        <UnorderedList>
                          {parks.map((park) => {
                            const facilityCount = park.facilitySubName
                              ? scopedFacilityCounts[park.facilitySubName] || 0
                              : park.scanners.reduce(
                                  (count, scanner) =>
                                    count +
                                    (facilityCountsByScanner[scanner] || 0),
                                  0,
                                );
                            return (
                              <UnorderedListItem key={park.href}>
                                <Link isExternal href={park.href}>
                                  {park.name}
                                </Link>{" "}
                                <Text as="span" color="subtle">
                                  — {facilityCount.toLocaleString()}{" "}
                                  {facilityCount === 1
                                    ? "facility"
                                    : "facilities"}
                                </Text>
                              </UnorderedListItem>
                            );
                          })}
                        </UnorderedList>
                      </Box>
                    ))}
                  </VStack>
                ))}
              </SimpleGrid>
              <Text fontSize="sm" mt={6}>
                Some spots (especially backcountry sites) might be hiding under
                a different search term. If you know of a site and can't find
                it, email us at{" "}
                <Link
                  isExternal
                  href="mailto:help@schnerp.com?subject=Need%20help%20finding%20a%20campground"
                >
                  help@schnerp.com
                </Link>{" "}
                and we'll help you out.
              </Text>
              <Box as="details" mt={4} fontSize="sm" color="subtle">
                <Box as="summary" cursor="pointer">
                  Using a trip-planning assistant?
                </Box>
                <Text mt={2}>
                  It can open a campground request form with your dates and site
                  type ready for you to review. Direct campground links accept
                  comma-separated <Code>dates</Code> in <Code>YYYY-MM-DD</Code>{" "}
                  format and a <Code>siteType</Code> shown on that campground's
                  form. You always enter your own email address.
                </Text>
              </Box>
            </ListItemContent>
          </ListItem>
        </List>
      </Content>
    </Layout>
  );
}
